import React, { useState, FormEvent, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import './App.css';

// Define the structure of a message object
interface Message {
  sender: 'user' | 'assistant';
  text: string;
  image?: string;
}

// Custom renderer for links to open them in a new tab
const LinkRenderer = (props: any) => {
  return (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  );
};

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null); // Ref for the chat window div

  // --- Auto-scrolling effect ---
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    // We target the last element in the chat window
    if (chatWindow && chatWindow.lastElementChild) {
      // scrollIntoView with 'start' aligns the top of the element with the top of the visible area
      chatWindow.lastElementChild.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages]); // Trigger effect whenever messages change

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() && !imageFile) return;

    const userMessage: Message = {
      sender: 'user',
      text: inputValue,
      image: imageFile ? URL.createObjectURL(imageFile) : undefined
    };

    // Add new message to state
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    // Clear input fields
    setInputValue('');
    setImageFile(null); // Clear the file input after sending

    // --- Backend Communication ---
    const formData = new FormData();
    // Pass the updated conversation history
    formData.append('history', JSON.stringify(newMessages.slice(0, -1))); // Send history *before* the new message
    formData.append('text', inputValue);
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const assistantMessage: Message = { sender: 'assistant', text: data.response };
      setMessages(prevMessages => [...prevMessages, assistantMessage]);

    } catch (error) {
      console.error("Failed to send message:", error);
      const errorMessage: Message = {
        sender: 'assistant',
        text: 'Ne pare rău, întâmpinăm probleme la conectarea cu serverul. Vă rugăm să încercați din nou mai târziu.'
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="App">
      <header className="header">
        <div className="logo">G2D</div>
        <h1>Asistent Virtual Design</h1>
      </header>
      <div className="chat-window" ref={chatWindowRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.sender === 'user' ? 'user-message' : 'assistant-message'}`}>
            {msg.sender === 'user' ? (
              <>
                {msg.text}
                {msg.image && <img src={msg.image} alt="User upload" className="chat-image" />}
              </>
            ) : (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{ a: LinkRenderer }} // Use the custom link renderer
              >
                {msg.text}
              </ReactMarkdown>
            )}
          </div>
        ))}
      </div>
      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageChange}
          style={{ display: 'none' }}
        />
        <button type="button" onClick={triggerFileSelect} className="icon-button upload-button">
          <span>📷</span>
          <span className="upload-button-text">Încarcă</span>
        </button>
        <input
          type="text"
          className="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Descrie stilul tău sau încarcă o fotografie..."
        />
        <button type="submit" className="icon-button">
          <span>➤</span>
        </button>
      </form>
    </div>
  );
}

export default App;